import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { listLookup } from '../../api/masterData';

interface BulkStatusDialogProps {
  open: boolean;
  leadCount: number;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (leadStatusId: number) => Promise<void>;
}

export const BulkStatusDialog = ({ open, leadCount, errorMessage, onClose, onSubmit }: BulkStatusDialogProps) => {
  const [leadStatusId, setLeadStatusId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);

  const statusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-statuses'],
    queryFn: () => listLookup('/master-data/lead-statuses'),
    enabled: open,
  });

  const handleSubmit = async () => {
    if (!leadStatusId) return;
    setSubmitting(true);
    try {
      await onSubmit(leadStatusId);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Update status of {leadCount} lead(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Choose the new status for the selected leads.
          </Typography>
          <TextField
            select
            label="New status"
            fullWidth
            value={leadStatusId}
            onChange={(e) => setLeadStatusId(Number(e.target.value))}
          >
            {statusesQuery.data?.map((s) => (
              <MenuItem key={s.id as number} value={s.id as number}>
                {String(s.name)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!leadStatusId || submitting} onClick={handleSubmit}>
          {submitting ? 'Updating…' : 'Update status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
